import { NORMALIZED_FILTER_OPERATORS } from "./constants";

type Args = Record<string, unknown>;

/** Trimmed and lowercased: how every email is stored and looked up. */
export const normalizeEmail = (email: string) => email.trim().toLowerCase();

/**
 * Returns `args` with the given email fields normalized wherever a Prisma
 * operation writes or matches a whole address: `data` (one row or many) and
 * upsert's `create` and `update`, including `{ set }`; and `where`, including
 * compound unique keys and AND/OR/NOT. Search operators like `contains` are
 * left alone. Nested writes and relation filters from other models aren't
 * reached, so a query through another model normalizes its own value.
 */
export const normalizeEmailArgs = <T>(
  args: T,
  fields: readonly string[],
): T => {
  if (!args || typeof args !== "object") {
    return args;
  }
  const next = { ...(args as Args) };
  for (const key of ["data", "create", "update"]) {
    if (key in next) {
      next[key] = Array.isArray(next[key])
        ? (next[key] as unknown[]).map((row) => _normalizeData(row, fields))
        : _normalizeData(next[key], fields);
    }
  }
  if ("where" in next) {
    next.where = _normalizeWhere(next.where, fields);
  }
  return next as T;
};

const _normalizeValue = (value: unknown) =>
  typeof value === "string" ? normalizeEmail(value) : value;

const _normalizeData = (data: unknown, fields: readonly string[]) => {
  if (!data || typeof data !== "object") {
    return data;
  }
  const next = { ...(data as Args) };
  for (const field of fields) {
    const value = next[field];
    if (typeof value === "string") {
      next[field] = normalizeEmail(value);
    } else if (value && typeof value === "object" && "set" in value) {
      next[field] = { ...value, set: _normalizeValue(value.set) };
    }
  }
  return next;
};

const _normalizeFilter = (filter: unknown): unknown => {
  if (typeof filter === "string") {
    return normalizeEmail(filter);
  }
  if (!filter || typeof filter !== "object") {
    return filter;
  }
  const next = { ...(filter as Args) };
  for (const operator of NORMALIZED_FILTER_OPERATORS) {
    if (!(operator in next)) {
      continue;
    }
    const operand = next[operator];
    if (Array.isArray(operand)) {
      next[operator] = operand.map(_normalizeValue);
    } else if (operator === "not") {
      next[operator] = _normalizeFilter(operand);
    } else {
      next[operator] = _normalizeValue(operand);
    }
  }
  return next;
};

const _normalizeWhere = (
  where: unknown,
  fields: readonly string[],
): unknown => {
  if (Array.isArray(where)) {
    return where.map((clause) => _normalizeWhere(clause, fields));
  }
  if (!where || typeof where !== "object") {
    return where;
  }
  const next = { ...(where as Args) };
  for (const [key, value] of Object.entries(next)) {
    if (fields.includes(key)) {
      next[key] = _normalizeFilter(value);
    } else if (key === "AND" || key === "OR" || key === "NOT") {
      next[key] = _normalizeWhere(value, fields);
    } else if (
      value &&
      typeof value === "object" &&
      fields.some((field) => key.split("_").includes(field))
    ) {
      // A compound unique key such as `eventId_email: { eventId, email }`.
      next[key] = _normalizeData(value, fields);
    }
  }
  return next;
};
